// ============================================================
// WhoSmarter — UI strings
//
// Add a new language: add lib/i18n/locales/{code}.ts, import here,
// register in lib/i18n/locale-meta.ts.
// ============================================================

import { ar } from './locales/ar';
import { de } from './locales/de';
import { es } from './locales/es';
import { fr } from './locales/fr';
import { ja } from './locales/ja';

export const messages = {
  en: {
    app: {
      stepCreate: 'Create a custom quiz',
      stepShare: 'Invite your friends',
      stepPlay: 'Play',
      logoS: 'Tap the S',
    },
    footer: {
      privacy: 'Privacy',
      terms: 'Terms',
      refunds: 'Refunds',
      support: 'Support',
      pricing: 'Pricing',
      x: 'X',
      instagram: 'Instagram',
    },
    billing: {
      title: 'Plans & billing',
      subtitle: 'Joining a quiz is always free. Subscribe to create more quizzes.',
      planFree: 'Free',
      planBasic: 'Basic',
      planPremium: 'Premium',
      freeIncludes: '{n} quizzes to try — yours forever',
      quizzesPerMonth: '{n} quizzes per month',
      perMonth: '/month',
      currentPlan: 'Your current plan',
      subscribe: 'Subscribe',
      manage: 'Manage subscription',
      remainingMonthly: '{n} of {limit} quizzes left this month',
      remainingFree: '{n} of {limit} free quizzes left',
      paymentSuccess: 'Payment successful! Your plan is now active.',
      paymentActivating: 'Payment received — activating your plan…',
      paymentCancelled: 'Checkout was cancelled. No charge was made.',
      signInFirst: 'Sign in with Google to subscribe.',
      checkoutError: 'Could not start checkout. Please try again.',
      backHome: 'Back to home',
      quotaExceededTitle: 'You’ve used all your quizzes',
      quotaExceededBody: 'Upgrade your plan to keep creating quizzes. Joining games stays free.',
      seePlans: 'See plans',
      rematchQuotaExceeded: 'Replaying the same quiz — the host’s quiz limit is used up.',
    },
    auth: {
      signInTitle: 'Sign in to generate your quiz',
      signInHint: 'Only the host needs an account. Your guests join via your invite link — no sign-in required.',
      signInGoogle: 'Continue with Google',
      signInError: 'Sign-in failed. Please try again.',
      signedInAs: 'Signed in as {email}',
      signOut: 'Sign out',
      downloadIosApp: 'Download iOS app',
      loading: 'Loading…',
    },
    lang: {
      label: 'Language',
      en: 'English',
      ru: 'Russian',
      es: 'Español',
      fr: 'Français',
      de: 'Deutsch',
      ja: '日本語',
      ar: 'العربية',
    },
    setup: {
      title: 'Setup required before you can play',
      step1: 'Copy .env.local.example to .env.local',
      step2: 'Fill in your Supabase URL + Anon Key',
      step3: 'Add your OpenAI API key',
      step4: 'Run supabase/schema.sql in your Supabase SQL Editor',
      step5: 'Restart the dev server',
    },
    create: {
      topic: 'Topic',
      topicPlaceholder: 'e.g. 90s Cartoons, Science, Football',
      difficulty: 'Difficulty',
      difficultyEasy: 'Easy',
      difficultyMedium: 'Medium',
      difficultyHard: 'Hard',
      questions: 'Number of questions',
      modeTitle: 'Game mode',
      modeRegular: 'Every answer counts',
      modeRegularHint: 'Answer right away. You can change your pick until the timer ends, and the full time always stays — everyone who answers correctly scores. The round ends early only if everyone has answered.',
      modeHardcore: 'Only first answer counts',
      modeHardcoreHint: 'Only the first correct answer scores. Your answer locks the instant you submit — no changing it, and voice answers get one shot. Answers are ranked by a synced clock, so a faster connection barely matters.',
      answerTime: 'Answer time (seconds)',
      mcTitle: 'Multiple choice',
      mcHint: 'Four options per question — everyone picks, every correct answer scores',
      cameraTitle: 'Player cameras',
      cameraHint: 'Off = audio only (mics still work). On = everyone shares video.',
      submit: 'Create challenge',
      generating: 'Generating questions',
      errorEmptyTopic: 'Please enter a topic.',
      errorGenerate: 'Failed to generate questions.',
      errorCreate: 'Failed to create game in database.',
      errorGeneric: 'Something went wrong.',
      createdTitle: 'Quiz created',
      createdHint: 'Share this link with up to 5 friends, then enter the quiz.',
      shareLink: 'Shareable link',
      copy: 'Copy',
      copied: 'Copied',
      enterQuiz: 'Enter quiz',
      adjustShow: 'More',
      adjustHide: 'Hide',
      browseOpenGames: 'Browse open games',
      browseOpenGamesHint: 'Join a quiz that’s waiting for players',
      inviteOnlyTitle: 'Invite only',
      inviteOnlyHintOn: 'Only people with your link can join. Not shown in the public list.',
      inviteOnlyHintOff: 'Listed under Browse open games until you start the quiz.',
    },
    rooms: {
      title: 'Open games',
      empty: 'No open games right now.',
      refresh: 'Refresh',
      join: 'Join',
      back: 'Back',
      loading: 'Loading…',
      error: 'Could not load open games.',
      players: '{n}/{max}',
      agoMinutes: '{n} min ago',
      agoHours: '{n} h ago',
      agoJustNow: 'Just now',
      modeRegular: 'Every answer counts',
      modeHardcore: 'Only first answer counts',
      mc: 'Multiple choice',
      voice: 'Voice',
    },
    join: {
      title: 'Join the quiz',
      hostTitle: 'You are the host',
      subtitle: 'Pick a name so everyone can see your score.',
      namePlaceholder: 'Your name',
      button: 'Join',
      joining: 'Joining…',
      full: 'This game is full (up to 6 players).',
      errorEmptyName: 'Please enter your name.',
      banned: 'You can’t rejoin this game.',
    },
    lobby: {
      title: 'Lobby',
      players: 'Players',
      playersCount: '{n} / {max} players',
      you: 'you',
      host: 'Host',
      shareInvite: 'Share this link to invite players (up to 6 total)',
      waitingHost: 'Waiting for the host to start…',
      needMore: 'Waiting for at least one more player…',
      start: 'Start quiz',
      removePlayer: 'Remove',
      removeConfirm: 'Remove {name} from this game?',
      removeConfirmYes: 'Remove',
      removeConfirmNo: 'Cancel',
      removeError: 'Couldn’t remove that player. Try again.',
      listedPublicly: 'Visible in Browse until you start.',
    },
    game: {
      joining: 'Joining game',
      notFound: 'Game not found',
      notFoundHint: "Check the link and try again. If you're the host, create a new game.",
      backHome: 'Back to home',
      removedTitle: 'You were removed',
      removedBody: 'The host removed you from this game.',
      removedHome: 'Back to home',
      host: 'Host',
      guest: 'Guest',
      live: 'Live',
      offline: 'Offline',
      waitingCamera: 'Waiting for camera',
      cameraErrorPermission: 'Camera/mic blocked. Allow access for this site, then tap here.',
      cameraErrorInsecure: 'Camera needs a secure (HTTPS) link.',
      cameraErrorNotFound: 'No camera or mic found. Tap to retry.',
      cameraErrorBusy: 'Camera/mic is in use by another app. Tap to retry.',
      cameraErrorUnknown: 'Could not access camera/mic. Tap to retry.',
      cameraErrorTap: 'Tap to allow',
      answering: 'Answering',
      topic: 'Topic',
      question: 'Question',
      stealChance: 'Steal chance',
      getReady: 'Get ready',
      thinkHint: 'Read the question — answering unlocks for both at once',
      answerNow: 'Answer now',
      tileAnswered: 'Answered',
      tileThinking: 'Thinking…',
      youAnswering: 'You are answering',
      listeningAnswer: 'Listening to answer',
      checkingAnswer: 'Checking answers',
      speakNow: 'Speak now',
      theySpeaking: "They're speaking",
      secondsLeft: '{n}s remaining',
      opponentSteal: 'Opponent can steal this one',
      liveAnswer: 'Live answer',
      correctAnswer: 'Correct answer',
      speakAnswerNow: 'Speak your answer now',
      speakHint: 'Both players answer out loud — first correct answer wins',
      yourAnswer: 'Your answer',
      startSpeaking: 'Start speaking your answer…',
      holdToAnswer: 'Hold to answer',
      recording: 'Recording… release to submit',
      listening: 'Listening…',
      holdHint: 'Hold the button and say your answer',
      saidNothing: 'No answer',
      done: 'Done',
      doneOptional: 'Lock in now (optional)',
      autoSubmitHint: 'Your spoken answer is saved automatically — no need to press anything.',
      answerLockedIn: 'Answer locked in',
      waitingOpponent: 'Waiting for the other player…',
      typeYourAnswer: 'Type your answer below',
      typePlaceholder: 'Or type your answer here',
      waitShareLink: 'Share the link with your opponent to start',
      waitHostStart: 'Waiting for the host to start',
      bothIn: 'Both players are in',
      questionsCount: '{n} questions',
      modeMc: 'Multiple choice',
      modeVoice: 'Speak your answer',
      startQuiz: 'Start quiz',
      youreIn: "You're in",
      waitHostStartOverlay: 'Waiting for the host to start',
      waitOpponent: 'Waiting for your opponent to join',
      connecting: 'Connecting to game',
      pointsMultiplier: '(×{n})',
    },
    buzz: {
      label: 'Buzz',
      tapFirst: 'Tap to answer first',
      youBuzzed: 'You buzzed, start speaking',
      otherAnswering: 'Other player is answering',
      ariaBuzz: 'Buzz in to answer',
      ariaDisabled: 'Buzz not available',
    },
    diag: {
      connected: 'Connected',
      connecting: 'Connecting…',
      micOff: 'No microphone detected',
      micStalled: 'No audio coming through',
      micActive: 'Microphone active',
      micIdle: 'Microphone muted',
      cameraOff: 'No camera',
    },
    ptt: {
      hold: 'Hold to talk',
      talking: 'Talking…',
      answerLive: 'Mic live',
    },
    mc: {
      opponentSteal: 'Opponent can steal this one',
      answerLocked: 'Answer locked in',
      canChange: 'You can still change',
      someoneAnswered: 'Someone answered — {n}s to answer!',
      you: 'You',
    },
    score: {
      vs: 'VS',
      streak: '{n} streak',
      leaderboard: 'Scores',
      points: 'pts',
    },
    winner: {
      result: 'Result',
      winner: 'Winner',
      winners: 'Winners',
      tie: 'Tie',
      finalScores: 'Final scores',
      downloadClips: 'Download answer clips',
      clipLabel: 'Q{n} · {role} answer',
      download: 'Download',
      rematch: 'Rematch',
      rematchWaiting: 'Waiting for others…',
      waitRematch: 'Waiting for the host to start a rematch',
      exit: 'Exit',
      discuss: 'Discuss results — call ends in {n}s',
      discussHint: 'Camera and mic have been turned off. Rematch to call again.',
    },
  },

  ru: {
    app: {
      stepCreate: 'Создайте свой квиз',
      stepShare: 'Пригласите друзей',
      stepPlay: 'Играйте',
      logoS: 'Нажми на S',
    },
    footer: {
      privacy: 'Конфиденциальность',
      terms: 'Условия',
      refunds: 'Возвраты',
      support: 'Поддержка',
      pricing: 'Тарифы',
      x: 'X',
      instagram: 'Instagram',
    },
    billing: {
      title: 'Тарифы и оплата',
      subtitle: 'Присоединяться к викторинам всегда бесплатно. Подписка нужна, чтобы создавать больше викторин.',
      planFree: 'Free',
      planBasic: 'Basic',
      planPremium: 'Premium',
      freeIncludes: '{n} викторин для пробы — навсегда',
      quizzesPerMonth: '{n} викторин в месяц',
      perMonth: '/мес',
      currentPlan: 'Ваш текущий тариф',
      subscribe: 'Подписаться',
      manage: 'Управлять подпиской',
      remainingMonthly: 'Осталось {n} из {limit} викторин в этом месяце',
      remainingFree: 'Осталось {n} из {limit} бесплатных викторин',
      paymentSuccess: 'Оплата прошла! Тариф активирован.',
      paymentActivating: 'Платёж получен — активируем тариф…',
      paymentCancelled: 'Оплата отменена. Деньги не списаны.',
      signInFirst: 'Войдите через Google, чтобы оформить подписку.',
      checkoutError: 'Не удалось открыть оплату. Попробуйте ещё раз.',
      backHome: 'На главную',
      quotaExceededTitle: 'Викторины на этот период закончились',
      quotaExceededBody: 'Перейдите на платный тариф, чтобы создавать больше викторин. Участие в играх остаётся бесплатным.',
      seePlans: 'Смотреть тарифы',
      rematchQuotaExceeded: 'Переигрываем ту же викторину — лимит создателя закончился.',
    },
    auth: {
      signInTitle: 'Войдите, чтобы создать викторину',
      signInHint: 'Аккаунт нужен только ведущему. Гости присоединяются по ссылке — вход не требуется.',
      signInGoogle: 'Продолжить с Google',
      signInError: 'Не удалось войти. Попробуйте снова.',
      signedInAs: 'Вы вошли как {email}',
      signOut: 'Выйти',
      downloadIosApp: 'Скачать iOS-приложение',
      loading: 'Загрузка…',
    },
    lang: {
      label: 'Язык',
      en: 'English',
      ru: 'Русский',
      es: 'Español',
      fr: 'Français',
      de: 'Deutsch',
      ja: '日本語',
      ar: 'العربية',
    },
    setup: {
      title: 'Нужна настройка перед игрой',
      step1: 'Скопируйте .env.local.example в .env.local',
      step2: 'Укажите Supabase URL и Anon Key',
      step3: 'Добавьте ключ OpenAI API',
      step4: 'Запустите supabase/schema.sql в SQL Editor Supabase',
      step5: 'Перезапустите dev-сервер',
    },
    create: {
      topic: 'Тема',
      topicPlaceholder: 'например: 90-е мультфильмы, наука, футбол',
      difficulty: 'Сложность',
      difficultyEasy: 'Лёгкая',
      difficultyMedium: 'Средняя',
      difficultyHard: 'Сложная',
      questions: 'Количество вопросов',
      modeTitle: 'Режим игры',
      modeRegular: 'Засчитывается каждый ответ',
      modeRegularHint: 'Отвечайте сразу. Ответ можно менять до конца таймера, время не сокращается — очко получают все, кто ответил верно. Раунд завершится раньше, только если ответят все.',
      modeHardcore: 'Засчитывается только первый ответ',
      modeHardcoreHint: 'Очко получает только тот, кто первым ответил верно. Ответ фиксируется сразу при отправке — менять нельзя, у голоса одна попытка. Порядок ответов определяется синхронизированным временем, поэтому скорость интернета почти не влияет.',
      answerTime: 'Время на ответ (сек)',
      mcTitle: 'Варианты ответа',
      mcHint: '4 варианта на вопрос — отвечают все, каждый верный ответ даёт очко',
      cameraTitle: 'Камеры игроков',
      cameraHint: 'Выкл = только звук (микрофоны работают). Вкл = все делятся видео.',
      submit: 'Создать викторину',
      generating: 'Генерируем вопросы',
      errorEmptyTopic: 'Введите тему.',
      errorGenerate: 'Не удалось сгенерировать вопросы.',
      errorCreate: 'Не удалось создать игру в базе данных.',
      errorGeneric: 'Что-то пошло не так.',
      createdTitle: 'Викторина создана',
      createdHint: 'Отправьте ссылку до 5 друзьям, затем войдите в игру.',
      shareLink: 'Ссылка для игры',
      copy: 'Копировать',
      copied: 'Скопировано',
      enterQuiz: 'Войти в викторину',
      adjustShow: 'Ещё',
      adjustHide: 'Скрыть',
      browseOpenGames: 'Открытые игры',
      browseOpenGamesHint: 'Присоединиться к викторине, где ждут игроков',
      inviteOnlyTitle: 'Только по ссылке',
      inviteOnlyHintOn: 'Войти могут только по вашей ссылке. Игра не в общем списке.',
      inviteOnlyHintOff: 'Игра видна в «Открытых играх», пока вы не начнёте викторину.',
    },
    rooms: {
      title: 'Открытые игры',
      empty: 'Сейчас нет открытых игр.',
      refresh: 'Обновить',
      join: 'Войти',
      back: 'Назад',
      loading: 'Загрузка…',
      error: 'Не удалось загрузить список игр.',
      players: '{n}/{max}',
      agoMinutes: '{n} мин назад',
      agoHours: '{n} ч назад',
      agoJustNow: 'Только что',
      modeRegular: 'Засчитывается каждый ответ',
      modeHardcore: 'Засчитывается только первый ответ',
      mc: 'Варианты',
      voice: 'Голос',
    },
    join: {
      title: 'Войти в викторину',
      hostTitle: 'Вы — ведущий',
      subtitle: 'Выберите имя, чтобы все видели ваш счёт.',
      namePlaceholder: 'Ваше имя',
      button: 'Войти',
      joining: 'Входим…',
      full: 'Игра заполнена (до 6 игроков).',
      errorEmptyName: 'Введите имя.',
      banned: 'Вы не можете снова войти в эту игру.',
    },
    lobby: {
      title: 'Лобби',
      players: 'Игроки',
      playersCount: '{n} / {max} игроков',
      you: 'вы',
      host: 'Ведущий',
      shareInvite: 'Отправьте ссылку, чтобы пригласить игроков (до 6 всего)',
      waitingHost: 'Ждём, когда ведущий начнёт…',
      needMore: 'Ждём ещё хотя бы одного игрока…',
      start: 'Начать викторину',
      removePlayer: 'Удалить',
      removeConfirm: 'Удалить {name} из этой игры?',
      removeConfirmYes: 'Удалить',
      removeConfirmNo: 'Отмена',
      removeError: 'Не удалось удалить игрока. Попробуйте снова.',
      listedPublicly: 'Видна в списке, пока вы не начнёте.',
    },
    game: {
      joining: 'Подключаемся к игре',
      notFound: 'Игра не найдена',
      notFoundHint: 'Проверьте ссылку. Если вы ведущий — создайте новую игру.',
      backHome: 'На главную',
      removedTitle: 'Вас удалили',
      removedBody: 'Ведущий удалил вас из этой игры.',
      removedHome: 'На главную',
      host: 'Ведущий',
      guest: 'Гость',
      live: 'В эфире',
      offline: 'Не в сети',
      waitingCamera: 'Ожидание камеры',
      cameraErrorPermission: 'Камера/мик заблокированы. Разрешите доступ для сайта и нажмите сюда.',
      cameraErrorInsecure: 'Нужна безопасная ссылка (HTTPS).',
      cameraErrorNotFound: 'Камера или микрофон не найдены. Нажмите, чтобы повторить.',
      cameraErrorBusy: 'Камера/мик заняты другим приложением. Нажмите, чтобы повторить.',
      cameraErrorUnknown: 'Нет доступа к камере/мику. Нажмите, чтобы повторить.',
      cameraErrorTap: 'Нажмите, чтобы разрешить',
      answering: 'Отвечает',
      topic: 'Тема',
      question: 'Вопрос',
      stealChance: 'Шанс перехвата',
      getReady: 'Приготовьтесь',
      thinkHint: 'Прочитайте вопрос — ответ откроется обоим одновременно',
      answerNow: 'Отвечайте',
      tileAnswered: 'Ответил',
      tileThinking: 'Думает…',
      youAnswering: 'Вы отвечаете',
      listeningAnswer: 'Слушаем ответ',
      checkingAnswer: 'Проверяем ответ',
      speakNow: 'Говорите',
      theySpeaking: 'Соперник отвечает',
      secondsLeft: 'осталось {n} с',
      opponentSteal: 'Соперник может перехватить',
      liveAnswer: 'Ответ в реальном времени',
      correctAnswer: 'Правильный ответ',
      speakAnswerNow: 'Говорите свой ответ',
      speakHint: 'Оба отвечают вслух — кто первый ответит верно, тот и выиграл',
      yourAnswer: 'Ваш ответ',
      startSpeaking: 'Произнесите свой ответ…',
      holdToAnswer: 'Удерживайте, чтобы ответить',
      recording: 'Запись… отпустите, чтобы отправить',
      listening: 'Слушаю…',
      holdHint: 'Удерживайте кнопку и произнесите ответ',
      saidNothing: 'Нет ответа',
      done: 'Готово',
      doneOptional: 'Зафиксировать (необязательно)',
      autoSubmitHint: 'Ваш произнесённый ответ сохраняется автоматически — ничего нажимать не нужно.',
      answerLockedIn: 'Ответ принят',
      waitingOpponent: 'Ждём другого игрока…',
      typeYourAnswer: 'Напишите ответ ниже',
      typePlaceholder: 'Или напишите ответ здесь',
      waitShareLink: 'Отправьте ссылку сопернику, чтобы начать',
      waitHostStart: 'Ждём начала от ведущего',
      bothIn: 'Оба игрока на месте',
      questionsCount: '{n} вопросов',
      modeMc: 'Варианты ответа',
      modeVoice: 'Ответ голосом',
      startQuiz: 'Начать викторину',
      youreIn: 'Вы в игре',
      waitHostStartOverlay: 'Ждём начала от ведущего',
      waitOpponent: 'Ждём подключения соперника',
      connecting: 'Подключаемся к игре',
      pointsMultiplier: '(×{n})',
    },
    buzz: {
      label: 'Базз',
      tapFirst: 'Нажмите первым, чтобы ответить',
      youBuzzed: 'Вы нажали, начинайте говорить',
      otherAnswering: 'Соперник отвечает',
      ariaBuzz: 'Нажать, чтобы ответить',
      ariaDisabled: 'Buzz недоступен',
    },
    diag: {
      connected: 'Соединено',
      connecting: 'Подключение…',
      micOff: 'Микрофон не найден',
      micStalled: 'Звук не поступает',
      micActive: 'Микрофон активен',
      micIdle: 'Микрофон выключен',
      cameraOff: 'Нет камеры',
    },
    ptt: {
      hold: 'Зажмите, чтобы говорить',
      talking: 'Говорите…',
      answerLive: 'Микрофон вкл',
    },
    mc: {
      opponentSteal: 'Соперник может перехватить',
      answerLocked: 'Ответ зафиксирован',
      canChange: 'Можно ещё поменять',
      someoneAnswered: 'Кто-то ответил — {n}с на ответ!',
      you: 'Вы',
    },
    score: {
      vs: 'VS',
      streak: 'серия {n}',
      leaderboard: 'Счёт',
      points: 'очк.',
    },
    winner: {
      result: 'Итог',
      winner: 'Победитель',
      winners: 'Победители',
      tie: 'Ничья',
      finalScores: 'Итоговый счёт',
      downloadClips: 'Скачать записи ответов',
      clipLabel: 'В{n} · ответ: {role}',
      download: 'Скачать',
      rematch: 'Реванш',
      rematchWaiting: 'Ждём остальных…',
      waitRematch: 'Ждём реванш от ведущего',
      exit: 'Выход',
      discuss: 'Обсудите результаты — звонок завершится через {n} с',
      discussHint: 'Камера и микрофон отключены. Начните реванш, чтобы снова позвонить.',
    },
  },

  es,
  fr,
  de,
  ja,
  ar,
} as const;

export type Locale = keyof typeof messages;
